import React, { useEffect } from "react";

import BannerIcon from "@uui/AlertSquare";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";

import { Typography, Box } from "@mui/material";

import TermsSection from "@app/analytics/auth/components/TermsSection";
import {
  DOMAIN_ERROR_TOAST,
  DUPLICATE_ACCOUNT_TOAST,
  isDomainError,
  requiredWhenEmail,
} from "@app/analytics/auth/utils";
import useDetectedLogin from "@app/analytics/hooks/sso/useDetectedLogin";
import useAuth from "@app/common/auth/useAuth";
import { toast } from "@app/common/hooks/useToasts";
import type { SignInData } from "@app/common/types";
import { yupResolver } from "@app/utils/forms/yupResolver";
import { usePageTitle } from "@app/utils/hooks";

import InfoPanel from "@rui/InfoPanel";
import Stack, { Row } from "@rui/Stack";

import IdentifyFirstSignIn from "./IdentifyFirstSignIn";
import type { SignInFormData } from "./types";
import AuthCardBanner from "../AuthCardBanner";

const getHeaderText = (queryParams: URLSearchParams) => {
  const workspaceId = queryParams.get("workspaceId");
  if (workspaceId) {
    return "Sign in to join this Workspace";
  }

  const sessionId = queryParams.get("sessionId");
  if (sessionId) {
    return "Sign in to access this Meeting Report";
  }

  const source = queryParams.get("source");
  if (source === "meet") {
    return "If you signed up through the Google Meet Add-on, you can sign in with Google.";
  }

  return null;
};

const signinValidationSchema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email")
    .when("action", requiredWhenEmail),
  password: yup.string().when("action", requiredWhenEmail),
});

const SigninCard = ({ standalone = false }: { standalone?: boolean }) => {
  usePageTitle("Sign in");

  const auth = useAuth();
  const { persistedLogin } = useDetectedLogin();

  const [queryParams] = useSearchParams();
  const workspaceId = queryParams.get("workspaceId");
  const errorReason = queryParams.get("reason");
  const accountAlreadyExists = queryParams.get("error") === "account_exists";

  const onSubmit = (data: SignInData) => {
    auth.signIn(data, {
      onError: async (error) => {
        const isError = await isDomainError(error);
        if (isError) {
          toast.show(DOMAIN_ERROR_TOAST);
        }
      },
    });
  };

  const formResolver = yupResolver(signinValidationSchema);
  const form = useForm<SignInFormData>({
    resolver: formResolver,
    defaultValues: persistedLogin?.email
      ? { email: persistedLogin.email }
      : undefined,
  });

  useEffect(() => {
    if (!workspaceId) return;
    toast.show({
      message: "To accept your Workspace invitation, please sign in.",
      sticky: true,
      dismissible: false,
    });
    return () => toast.hide();
  }, [workspaceId]);

  useEffect(() => {
    if (errorReason === "domain_capture") {
      toast.show(DOMAIN_ERROR_TOAST);
    } else if (errorReason === "duplicate") {
      toast.show(DUPLICATE_ACCOUNT_TOAST);
    }
  }, [errorReason]);

  const bannerText = getHeaderText(queryParams);
  const handleSubmit = form.handleSubmit(onSubmit);

  return (
    <>
      {bannerText && (
        <AuthCardBanner icon={<BannerIcon />}>{bannerText}</AuthCardBanner>
      )}
      <Box component="form" onSubmit={handleSubmit}>
        {/* use column reverse so that pressing enter will select the sign-in button */}
        <Stack sx={{ width: "100%" }} direction="column-reverse">
          <Row variant="center">
            <TermsSection context="signin" />
          </Row>
          <IdentifyFirstSignIn
            form={form}
            isLoading={auth.isLoading}
            persistedLogin={persistedLogin}
            standalone={standalone}
            onOAuthClick={handleSubmit}
          />
          {accountAlreadyExists && (
            <InfoPanel severity="warning" sx={{ mb: 3 }}>
              <Typography variant="bodySecondaryMed" color="warning.main">
                An account with that email already exists. Please sign in below.
              </Typography>
            </InfoPanel>
          )}
        </Stack>
      </Box>
    </>
  );
};

export default SigninCard;
